import { useState } from 'react';

import { SearchBar } from '@/components/common/search-bar';
import { Tabs } from '@/components/common/tabs';

import type { ApplicableEntityType } from '../types';

import { DiscountCustomersTab } from './discount-customers-tab';
import { DiscountEntitiesTab } from './discount-entities-tab';
import styles from './discount-relationship-tabs.module.scss';

const LABEL: Record<ApplicableEntityType, string> = {
  customer: 'Customers',
  device: 'Devices',
  bundle: 'Bundles',
};

export type DiscountRelationshipTabsProps = {
  discountId: string;
  tabs: ApplicableEntityType[];
};

/** Right-column relationship tabs for the discount detail page (ticket 06). Underline tab strip
 *  (left) + a per-tab search input (top-right); each tab keeps its own search term across switches
 *  via the `key={activeKey}` remount that re-seeds the SearchBar from that tab's saved value. */
export function DiscountRelationshipTabs({ discountId, tabs }: DiscountRelationshipTabsProps) {
  const defaultActive = tabs.includes('customer') ? 'customer' : tabs[0];
  const [activeKey, setActiveKey] = useState<ApplicableEntityType>(defaultActive ?? 'customer');
  const [search, setSearch] = useState<Record<ApplicableEntityType, string>>({
    customer: '',
    device: '',
    bundle: '',
  });

  if (tabs.length === 0) return null;

  const items = tabs.map((type) => ({
    key: type,
    label: LABEL[type],
    children:
      type === 'customer' ? (
        <DiscountCustomersTab discountId={discountId} search={search.customer} />
      ) : (
        <DiscountEntitiesTab discountId={discountId} type={type} search={search[type]} />
      ),
  }));

  return (
    <Tabs
      className={styles.tabs}
      variant="underline"
      activeKey={activeKey}
      onChange={(k) => setActiveKey(k as ApplicableEntityType)}
      items={items}
      tabBarExtraContent={{
        right: (
          <SearchBar
            key={activeKey}
            defaultValue={search[activeKey]}
            placeholder="Search"
            aria-label="Search"
            onSearch={(term) => setSearch((s) => ({ ...s, [activeKey]: term }))}
          />
        ),
      }}
    />
  );
}
